import { ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import { flatPages } from "@/components/nav";
import { shortcutsFor } from "@/pages/home/shortcuts";
import type { Archetype, HomeViewer } from "@/pages/home/types";

function greetingFor(hour: number): string {
  if (hour < 5) return "Working late";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

/** Chrome shared by every archetype: the greeting header and the jump strip.
 *
 *  Both the greeting and the date come from the server's Tbilisi clock, not
 *  `new Date()`. The old page read the browser, so a laptop on another
 *  timezone could show "Good evening" above a day's numbers that had already
 *  rolled over. */
export function HomeShell({
  viewer,
  serverNow,
  archetype,
  allowed,
  actions,
  children,
}: {
  viewer: HomeViewer;
  serverNow: string;
  archetype: Archetype;
  allowed: Set<string>;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const now = useMemo(() => new Date(serverNow), [serverNow]);
  const firstName = (viewer.full_name || viewer.email).trim().split(/\s+/)[0];
  // serverNow carries a +04:00 offset, so these read Tbilisi wall-clock.
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hour12: false,
      timeZone: "Asia/Tbilisi",
    }).format(now),
  );
  const longDate = new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "Asia/Tbilisi",
  }).format(now);

  const jumps = useMemo(
    () => shortcutsFor(archetype, allowed),
    [archetype, allowed],
  );
  const pages = useMemo(() => {
    const byPath = new Map(flatPages(allowed).map((p) => [p.to, p]));
    return jumps.map((to) => byPath.get(to)).filter(Boolean) as ReturnType<
      typeof flatPages
    >;
  }, [jumps, allowed]);

  return (
    <div className="w-full">
      {/* Deliberately tighter than the app-wide PageHeader: Home's priority is
          fitting one screen, and a 22px title with its own bottom rule cost
          ~90px before any content. */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3.5">
        <div className="min-w-0">
          <h1 className="text-[17px] font-semibold text-slate-100 leading-tight truncate">
            {greetingFor(hour)}, {firstName}
          </h1>
          <p className="text-[12px] text-slate-500 mt-0.5">{longDate}</p>
        </div>
        {actions}
      </div>
      {children}
      {pages.length > 0 && (
        // Anchored in a panel with a label, not floating pills. Loose buttons
        // sitting in whitespace under the content read as leftovers rather
        // than as a deliberate row of destinations.
        <div className="card mt-3 px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Jump to
            </span>
            <div className="flex flex-wrap gap-2">
              {pages.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  className="group flex items-center gap-1.5 rounded-md border border-white/5 px-2.5 py-1 hover:border-white/15 hover:bg-white/[0.03] transition"
                >
                  <span className="text-slate-500 group-hover:text-accent-300 transition">
                    <p.icon />
                  </span>
                  <span className="text-[12px] text-slate-300">{p.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
